import { useCallback, useEffect } from 'react';
import { useSetImmerAtom } from 'jotai-immer';
import { useImmerAtom } from 'jotai-immer';
import {
    monitorStatusTableStateAtom,
    monitorStatusTableViewAtom,
    monitorStatusTableStatsAtom,
    monitorStatusTableFiltersAtom,
    monitorStatusDataAtom,
} from './monitorstatus_atoms';
import { monitorStatusColumnSpec, monitorStatusDefaultView } from './MonitorStatusTableSpec';
import { tblSpecLoadView } from '../../../../core/table/tableViewsRepo';
import { tableViewVM, tableStateVM, tableStatsVM } from '../../../../core/table/table_view_models';
import { MonitorItem } from '../../data/datamonitorRepo';
import datamonitorRepo from '../../data/datamonitorRepo';
import { DatafeedQuery } from '../../data/datamonitorRepo';

type UseMonitorStatusTableInitReturn = {
    init: boolean;
    loadMonitors: (ce: string, monitorTypes: string[], presetMonitorType?: string) => void;
    loadVerityMonitors: (query: DatafeedQuery) => void;
};

export const useMonitorStatusTableInit = (
    monitorTypes: string[],
    onSubscribeToggle?: (item: any, colKey: string, newVal: boolean) => Promise<boolean>,
    isVerity = false,
): UseMonitorStatusTableInitReturn => {
    const [{ init }, setState] = useImmerAtom(monitorStatusTableStateAtom);
    const setView = useSetImmerAtom(monitorStatusTableViewAtom);
    const setStats = useSetImmerAtom(monitorStatusTableStatsAtom);
    const setFilter = useSetImmerAtom(monitorStatusTableFiltersAtom);
    const setData = useSetImmerAtom(monitorStatusDataAtom);

    useEffect(() => {
        if (init) {
            const colSpec = monitorStatusColumnSpec(monitorTypes, onSubscribeToggle, isVerity);
            const defaultView = monitorStatusDefaultView();
            const view = tblSpecLoadView(defaultView.key, [defaultView], colSpec);
            setView(() => view || tableViewVM());
            if (defaultView.filterby) {
                const presetFilters = defaultView.filterby.reduce(
                    (acc, f) => {
                        acc[f.key] = f.val;
                        return acc;
                    },
                    {} as Record<string, any>,
                );
                setFilter(() => presetFilters);
            } else {
                setFilter(() => ({}));
            }
            setStats(() => tableStatsVM());
            setState(draft => {
                draft.views = [defaultView];
                draft.currentViewId = defaultView.key;
                draft.init = false;
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [init, monitorTypes, onSubscribeToggle, setState, setView, setFilter, setStats]);

    const loadMonitors = useCallback(
        async (ce: string, monitorTypes: string[], presetMonitorType?: string): Promise<void> => {
            // Reset filters, stats and re-apply default view filterby before fetching.
            // A deep-link preset overrides the reset with the alert's MonitorType column filter.
            if (presetMonitorType) {
                setFilter(() => ({ MonitorType: [presetMonitorType] }));
            } else {
                const defaultView = monitorStatusDefaultView();
                if (defaultView.filterby) {
                    const presetFilters = defaultView.filterby.reduce(
                        (acc, f) => {
                            acc[f.key] = f.val;
                            return acc;
                        },
                        {} as Record<string, any>,
                    );
                    setFilter(() => presetFilters);
                } else {
                    setFilter(() => ({}));
                }
            }
            setStats(() => tableStatsVM());
            setState(draft => {
                draft.waiting = true;
            });
            try {
                const items = await datamonitorRepo.getMonitors(ce, monitorTypes);
                setData(draft => {
                    draft.dataItems = items as MonitorItem[];
                    draft.items = [];
                });
            } catch {
                setData(() => ({ items: [], dataItems: [] }));
            } finally {
                setState(draft => {
                    draft.waiting = false;
                });
            }
        },
        [setState, setData, setFilter, setStats],
    );

    // VERITY query-driven load (Monitor Status search form)
    const loadVerityMonitors = useCallback(
        async (query: DatafeedQuery): Promise<void> => {
            setFilter(() => ({}));
            setStats(() => tableStatsVM());
            setState(draft => {
                draft.waiting = true;
            });
            try {
                const items = await datamonitorRepo.getVerityMonitors(query);
                setData(draft => {
                    draft.dataItems = items as MonitorItem[];
                    draft.items = [];
                });
            } catch {
                setData(() => ({ items: [], dataItems: [] }));
            } finally {
                setState(draft => {
                    draft.waiting = false;
                });
            }
        },
        [setState, setData, setFilter, setStats],
    );

    // Cleanup on unmount
    useEffect(() => {
        return (): void => {
            setState(() => tableStateVM());
            setView(() => tableViewVM());
            setFilter(() => ({}));
            setStats(() => tableStatsVM());
            setData(() => ({ items: [], dataItems: [] }));
        };
    }, [setState, setView, setFilter, setStats, setData]);

    return { init, loadMonitors, loadVerityMonitors };
};
